import { useCallback, useEffect, useRef, useState } from "react";

import {
  Circle,
  Eraser,
  FlipHorizontal2,
  PaintBucket,
  Pencil,
  Redo2,
  Save,
  Square,
  Trash2,
  Undo2,
} from "lucide-react";

import { Modal } from "../../components/Modal";

import {
  CANVAS_PX,
  CELL_PX,
  COLS,
  PALETTE_16,
  ROWS,
  canRedo,
  canUndo,
  commit,
  createGrid,
  createHistory,
  drawShape,
  floodFill,
  isDirty,
  isEmptyGrid,
  loadRecentColors,
  normalizeColor,
  paintGrid,
  paintLine,
  pushRecentColor,
  redo,
  saveRecentColors,
  undo,
} from "./pixelGrid";

import type { PixelGrid, Point, PixelHistory } from "./pixelGrid";

type Tool = "pen" | "eraser" | "fill" | "rect" | "ellipse";

type Confirm = "reset" | "close" | null;

type Stroke = {
  pointerId: number;
  tool: Tool;
  base: PixelGrid;
  start: Point;
  last: Point;
  color: string;
};

const TOOLS: { tool: Tool; label: string; Icon: typeof Pencil }[] = [
  { tool: "pen", label: "펜", Icon: Pencil },
  { tool: "eraser", label: "지우개", Icon: Eraser },
  { tool: "fill", label: "채우기", Icon: PaintBucket },
  { tool: "rect", label: "네모", Icon: Square },
  { tool: "ellipse", label: "동그라미", Icon: Circle },
];

const clampCell = (point: Point, grid: PixelGrid): Point => ({
  x: Math.min(grid.cols - 1, Math.max(0, point.x)),
  y: Math.min(grid.rows - 1, Math.max(0, point.y)),
});

export type PixelArtModalProps = {
  /** 있으면 고치기 모드: 이 격자가 처음 상태(baseline)가 된다. */
  initialGrid?: PixelGrid;
  /** 격자만 돌려준다. PNG 만들기와 장면에 넣기는 부른 쪽 몫이다. */
  onSave: (grid: PixelGrid) => void | Promise<void>;
  onClose: () => void;
};

export const PixelArtModal = ({
  initialGrid,
  onSave,
  onClose,
}: PixelArtModalProps) => {
  const [history, setHistory] = useState<PixelHistory>(() =>
    createHistory(initialGrid ?? createGrid()),
  );
  const historyRef = useRef(history);
  const [tool, setTool] = useState<Tool>("pen");
  const [color, setColor] = useState("#000000");
  const [mirror, setMirror] = useState(false);
  const [recent, setRecent] = useState<string[]>(loadRecentColors);
  const recentRef = useRef(recent);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [pending, setPending] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLDivElement>(null);
  const safeButtonRef = useRef<HTMLButtonElement>(null);
  const hadConfirmRef = useRef(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const draftRef = useRef<PixelGrid | null>(null);
  const strokeRef = useRef<Stroke | null>(null);
  const frameRef = useRef<number | null>(null);
  const mountedRef = useRef(true);

  const present = history.present;

  const applyHistory = useCallback((next: PixelHistory) => {
    historyRef.current = next;
    setHistory(next);
  }, []);

  const draw = useCallback(() => {
    frameRef.current = null;
    const ctx = canvasRef.current?.getContext("2d");
    if (ctx) {
      paintGrid(ctx, draftRef.current ?? historyRef.current.present, CELL_PX);
    }
  }, []);

  const requestDraw = useCallback(() => {
    if (frameRef.current !== null) {
      return;
    }
    if (typeof window.requestAnimationFrame === "function") {
      frameRef.current = window.requestAnimationFrame(draw);
    } else {
      draw();
    }
  }, [draw]);

  useEffect(() => {
    requestDraw();
  }, [history, requestDraw]);

  useEffect(() => {
    mountedRef.current = true;
    rootRef.current?.focus();
    return () => {
      mountedRef.current = false;
      if (frameRef.current !== null) {
        window.cancelAnimationFrame?.(frameRef.current);
        // StrictMode가 effect를 한 번 비웠다 다시 켜도 다음 그리기가 막히지 않게.
        frameRef.current = null;
      }
    };
  }, []);

  /** 칸 좌표. 숫자가 아니거나 캔버스 폭이 0이면 null. */
  const cellAt = (clientX: number, clientY: number): Point | null => {
    const rect = canvasRef.current!.getBoundingClientRect();
    const x = Math.floor(((clientX - rect.left) / rect.width) * COLS);
    const y = Math.floor(((clientY - rect.top) / rect.height) * ROWS);
    return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
  };

  const pickColor = (picked: string) => {
    const normalized = normalizeColor(picked);
    if (!normalized) {
      return;
    }
    setColor(normalized);
    // 색을 고르면 지우개에서도 펜으로 돌아온다.
    setTool((current) => (current === "eraser" ? "pen" : current));
  };

  const rememberColor = (used: string) => {
    const next = pushRecentColor(recentRef.current, used);
    recentRef.current = next;
    saveRecentColors(next);
    setRecent(next);
  };

  const finishStroke = (stroke: Stroke, keep: boolean) => {
    strokeRef.current = null;
    const draft = draftRef.current;
    draftRef.current = null;
    if (keep && draft) {
      const next = commit(historyRef.current, draft);
      if (next !== historyRef.current) {
        applyHistory(next);
        if (stroke.tool !== "eraser") {
          rememberColor(stroke.color);
        }
      }
    }
    requestDraw();
  };

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (pending || event.button !== 0) {
      return;
    }
    const open = strokeRef.current;
    if (open) {
      if (open.pointerId !== event.pointerId) {
        // 다른 손가락: 멀티터치는 막는다.
        return;
      }
      // 같은 포인터의 up이 오지 않은 낡은 획: 취소와 같게 마무리하고 새로 시작한다.
      finishStroke(open, open.tool === "pen" || open.tool === "eraser");
    }
    const point = cellAt(event.clientX, event.clientY);
    if (!point) {
      return;
    }
    event.preventDefault();
    const base = historyRef.current.present;
    const stroke: Stroke = {
      pointerId: event.pointerId,
      tool,
      base,
      start: point,
      last: point,
      color,
    };
    strokeRef.current = stroke;
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      // 캡처에 실패해도 캔버스 위에서는 계속 그릴 수 있다.
    }
    if (tool === "pen" || tool === "eraser") {
      draftRef.current = paintLine(
        base,
        point,
        point,
        tool === "pen" ? color : null,
        mirror,
      );
    } else if (tool === "rect" || tool === "ellipse") {
      draftRef.current = drawShape(
        base,
        tool,
        clampCell(point, base),
        clampCell(point, base),
        color,
        mirror,
      );
    }
    requestDraw();
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const stroke = strokeRef.current;
    if (!stroke || stroke.pointerId !== event.pointerId) {
      return;
    }
    if (stroke.tool === "pen" || stroke.tool === "eraser") {
      const native = event.nativeEvent;
      const samples =
        typeof native.getCoalescedEvents === "function"
          ? native.getCoalescedEvents()
          : [];
      const events = samples.length > 0 ? samples : [native];
      let grid = draftRef.current ?? stroke.base;
      for (const sample of events) {
        const point = cellAt(sample.clientX, sample.clientY);
        if (!point) {
          continue;
        }
        grid = paintLine(
          grid,
          stroke.last,
          point,
          stroke.tool === "pen" ? stroke.color : null,
          mirror,
        );
        stroke.last = point;
      }
      draftRef.current = grid;
    } else if (stroke.tool === "rect" || stroke.tool === "ellipse") {
      const cell = cellAt(event.clientX, event.clientY);
      if (!cell) {
        return;
      }
      const end = clampCell(cell, stroke.base);
      draftRef.current = drawShape(
        stroke.base,
        stroke.tool,
        clampCell(stroke.start, stroke.base),
        end,
        stroke.color,
        mirror,
      );
    } else {
      return;
    }
    requestDraw();
  };

  const onPointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const stroke = strokeRef.current;
    if (!stroke || stroke.pointerId !== event.pointerId) {
      return;
    }
    if (stroke.tool === "fill") {
      const point = cellAt(event.clientX, event.clientY);
      const inside =
        point !== null &&
        point.x >= 0 &&
        point.y >= 0 &&
        point.x < COLS &&
        point.y < ROWS;
      draftRef.current =
        inside && point
          ? floodFill(stroke.base, point, stroke.color, mirror)
          : null;
      finishStroke(stroke, inside);
      return;
    }
    finishStroke(stroke, true);
  };

  const onPointerCancel = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const stroke = strokeRef.current;
    if (!stroke || stroke.pointerId !== event.pointerId) {
      return;
    }
    // 펜과 지우개는 지금까지 그린 것을 남기고, 도형과 채우기는 버린다.
    finishStroke(stroke, stroke.tool === "pen" || stroke.tool === "eraser");
  };

  // 획을 긋는 도중에는 기록을 건드리지 않는다.
  const doUndo = () => {
    if (!strokeRef.current) {
      applyHistory(undo(historyRef.current));
    }
  };
  const doRedo = () => {
    if (!strokeRef.current) {
      applyHistory(redo(historyRef.current));
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!(event.ctrlKey || event.metaKey) || pending) {
      return;
    }
    const key = event.key.toLowerCase();
    if (key === "z" && !event.shiftKey) {
      doUndo();
    } else if ((key === "z" && event.shiftKey) || key === "y") {
      doRedo();
    } else {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
  };

  useEffect(() => {
    if (confirm) {
      hadConfirmRef.current = true;
      confirmRef.current?.scrollIntoView?.({ block: "nearest" });
      safeButtonRef.current?.focus();
    } else if (hadConfirmRef.current) {
      // 확인 영역이 사라지면 초점이 body로 떨어지지 않게 모달 루트로 돌려준다.
      hadConfirmRef.current = false;
      rootRef.current?.focus();
    }
  }, [confirm]);

  const requestReset = () => {
    if (isEmptyGrid(historyRef.current.present)) {
      return;
    }
    setConfirm("reset");
  };

  const requestClose = useCallback(() => {
    if (pending) {
      return;
    }
    // Esc가 열려 있는 확인 영역을 먼저 닫는다.
    if (confirm) {
      setConfirm(null);
      return;
    }
    if (isDirty(historyRef.current)) {
      setConfirm("close");
    } else {
      onClose();
    }
  }, [confirm, onClose, pending]);

  const save = async () => {
    if (pending || isEmptyGrid(historyRef.current.present)) {
      return;
    }
    setPending(true);
    setSaveFailed(false);
    try {
      await onSave(historyRef.current.present);
    } catch {
      if (mountedRef.current) {
        setSaveFailed(true);
      }
    } finally {
      if (mountedRef.current) {
        setPending(false);
      }
    }
  };

  const busy = pending;

  return (
    <Modal
      title={initialGrid ? "픽셀 아트 고치기" : "픽셀 아트"}
      className="canvaschool-pixel-modal"
      primaryLabel="저장"
      hideActions
      pending={pending}
      onPrimary={() => void save()}
      onCancel={requestClose}
    >
      <div
        ref={rootRef}
        className="canvaschool-pixel"
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <div className="canvaschool-pixel-stage-col">
          <div className="canvaschool-pixel-stage">
            <canvas
              ref={canvasRef}
              className="canvaschool-pixel-canvas"
              width={CANVAS_PX}
              height={CANVAS_PX}
              aria-label="픽셀 아트 그림판"
              data-testid="pixel-art-canvas"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerCancel}
              onLostPointerCapture={onPointerCancel}
              onContextMenu={(event) => event.preventDefault()}
            />
            <div className="canvaschool-pixel-gridlines" aria-hidden="true" />
            {mirror && (
              <div
                className="canvaschool-pixel-guide"
                data-testid="pixel-art-guide"
                aria-hidden="true"
              />
            )}
          </div>
        </div>

        <div className="canvaschool-pixel-side">
          <div
            className="canvaschool-pixel-group"
            role="group"
            aria-label="도구"
          >
            {TOOLS.map(({ tool: id, label, Icon }) => (
              <button
                key={id}
                type="button"
                className="canvaschool-pixel-btn"
                aria-pressed={tool === id}
                disabled={busy}
                onClick={() => setTool(id)}
              >
                <Icon size={18} aria-hidden="true" />
                <span>{label}</span>
              </button>
            ))}
            <button
              type="button"
              className="canvaschool-pixel-btn"
              aria-pressed={mirror}
              disabled={busy}
              onClick={() => setMirror((current) => !current)}
            >
              <FlipHorizontal2 size={18} aria-hidden="true" />
              <span>대칭</span>
            </button>
          </div>

          <div className="canvaschool-pixel-group canvaschool-pixel-group--pair">
            <button
              type="button"
              className="canvaschool-pixel-btn"
              disabled={busy || !canUndo(history)}
              onClick={doUndo}
            >
              <Undo2 size={18} aria-hidden="true" />
              <span>되돌리기</span>
            </button>
            <button
              type="button"
              className="canvaschool-pixel-btn"
              disabled={busy || !canRedo(history)}
              onClick={doRedo}
            >
              <Redo2 size={18} aria-hidden="true" />
              <span>다시하기</span>
            </button>
          </div>

          <div
            className="canvaschool-pixel-palette"
            role="group"
            aria-label="색"
          >
            {PALETTE_16.map(({ color: hex, name }) => (
              <button
                key={hex}
                type="button"
                className="canvaschool-pixel-swatch"
                style={{ background: hex }}
                aria-label={name}
                aria-pressed={color === hex}
                disabled={busy}
                onClick={() => pickColor(hex)}
              />
            ))}
          </div>

          <div className="canvaschool-pixel-recent">
            <div className="canvaschool-pixel-picker" title="색 직접 고르기">
              <span aria-hidden="true" className="canvaschool-pixel-rainbow" />
              <input
                type="color"
                aria-label="색 직접 고르기"
                value={color}
                disabled={busy}
                onInput={(event) => pickColor(event.currentTarget.value)}
                onChange={(event) => pickColor(event.currentTarget.value)}
              />
            </div>
            {Array.from({ length: 4 }, (_, index) => {
              const hex = recent[index];
              return hex ? (
                <button
                  key={index}
                  type="button"
                  className="canvaschool-pixel-swatch"
                  style={{ background: hex }}
                  aria-label={`최근 색 ${index + 1}`}
                  aria-pressed={color === hex}
                  disabled={busy}
                  onClick={() => pickColor(hex)}
                />
              ) : (
                <span
                  key={index}
                  className="canvaschool-pixel-swatch canvaschool-pixel-swatch--empty"
                  aria-hidden="true"
                />
              );
            })}
          </div>

          {confirm && (
            <div
              ref={confirmRef}
              className="canvaschool-pixel-confirm"
              role="alertdialog"
              aria-label="확인"
            >
              <p>
                {confirm === "reset"
                  ? "그림을 모두 지우고 처음부터 그릴까요? 되돌리기로 다시 살릴 수 있어요."
                  : "저장하지 않은 그림이 있어요. 그래도 닫을까요?"}
              </p>
              <div className="canvaschool-pixel-confirm-actions">
                <button
                  ref={safeButtonRef}
                  type="button"
                  className="canvaschool-btn-secondary"
                  onClick={() => setConfirm(null)}
                >
                  계속 그리기
                </button>
                <button
                  type="button"
                  className="canvaschool-btn-danger"
                  onClick={() => {
                    if (confirm === "reset") {
                      applyHistory(commit(historyRef.current, createGrid()));
                      setConfirm(null);
                    } else {
                      onClose();
                    }
                  }}
                >
                  {confirm === "reset" ? "초기화" : "그래도 닫기"}
                </button>
              </div>
            </div>
          )}

          {saveFailed && (
            <p className="canvaschool-pixel-error" role="alert">
              저장하지 못했어요. 다시 눌러 보세요.
            </p>
          )}

          <div className="canvaschool-pixel-footer">
            <button
              type="button"
              className="canvaschool-pixel-btn"
              disabled={busy || isEmptyGrid(present)}
              onClick={requestReset}
            >
              <Trash2 size={18} aria-hidden="true" />
              <span>초기화</span>
            </button>
            <button
              type="button"
              className="canvaschool-btn-primary canvaschool-pixel-save"
              disabled={busy || isEmptyGrid(present)}
              onClick={() => void save()}
            >
              <Save size={18} aria-hidden="true" />
              <span>{pending ? "저장하는 중…" : "저장"}</span>
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
